import db from "../db/database";
import { markForDelete, markForSync } from "./syncService";

// Servicio para manejar las operaciones relacionadas con los productos

// Agregar un nuevo producto a la base de datos
export const addProduct = async (product) => {
    try {
        // Verificar si la descripción del producto ya existe
        const existingProduct = await db.products
            .where("description")
            .equals(product.description)
            .first();

        if (existingProduct) {
            throw new Error("Ya existe un producto con esa descripción");
        }

        // Verificar si el código de barras ya existe y no está vacío
        if (product.barcode) {
            const existingBarcode = await db.products
                .where("barcode")
                .equals(product.barcode)
                .and(product => !product.deleted)
                .first();

            if (existingBarcode) {
                throw new Error("Ya existe un producto con ese código de barras");
            }
        }

        // Agregar el producto a la base de datos
        const id = await db.products.add({
            description: product.description,
            Unit_Price: product.Unit_Price || 0, // Agregar precio unitario
            barcode: product.barcode || null, // Agregar código de barras
            PorIva: product.PorIva || 0, // Agregar porcentaje de IVA
            createdAt: new Date(),
            pendingSync: 1, // Marcar como pendiente de sincronización (usando 1 en lugar de true)
            lastModified: new Date(),
            No: product.No || null, // Guardar el No de Business Central si existe
            deleted: false,
        });

        return { id, ...product };
    } catch (error) {
        console.error("Error al agregar producto:", error);
        throw error;
    }
};

// Obtener todos los productos
export const getAllProducts = async () => {
    try {
        // Obtener solo productos no marcados como eliminados
        const products = await db.products
            .filter((product) => !product.deleted)
            .toArray();
        return products;
    } catch (error) {
        console.error("Error al obtener productos:", error);
        throw error;
    }
};

// Obtener un producto por ID local
export const getProductById = async (id) => {
    try {
        const product = await db.products.get(id);
        if (!product || product.deleted) {
            throw new Error("Producto no encontrado");
        }
        return product;
    } catch (error) {
        console.error(`Error al obtener producto con ID ${id}:`, error);
        throw error;
    }
};

// Obtener un producto por No de Business Central
export const getProductByNo = async (no) => {
    try {
        const product = await db.products
            .where("No")
            .equals(no)
            .and((product) => !product.deleted)
            .first();

        if (!product) {
            throw new Error("Producto no encontrado");
        }
        return product;
    } catch (error) {
        console.error(`Error al obtener producto con No ${no}:`, error);
        throw error;
    }
};

// Obtener un producto por código de barras
export const getProductByBarcode = async (barcode) => {
    try {
        const product = await db.products
            .where("barcode")
            .equals(barcode)
            .and((product) => !product.deleted)
            .first();

        if (!product) {
            return null; // Devolver null en lugar de lanzar error para facilitar la validación
        }
        return product;
    } catch (error) {
        console.error(`Error al obtener producto con código de barras ${barcode}:`, error);
        throw error;
    }
};

// Actualizar un producto existente
export const updateProduct = async (id, productData) => {
    try {
        const product = await db.products.get(id);
        if (!product || product.deleted) {
            throw new Error("Producto no encontrado");
        }

        // Verificar si el código de barras ya existe y no está vacío
        if (productData.barcode && productData.barcode !== product.barcode) {
            const existingBarcode = await db.products
                .where("barcode")
                .equals(productData.barcode)
                .and(p => !p.deleted && p.id !== id)
                .first();

            if (existingBarcode) {
                throw new Error("Ya existe un producto con ese código de barras");
            }
        }

        // Actualizar el producto
        await db.products.update(id, {
            ...productData,
            lastModified: new Date(),
        });

        // Marcar como pendiente de sincronización
        await markForSync("products", id);

        return await getProductById(id);
    } catch (error) {
        console.error(`Error al actualizar producto con ID ${id}:`, error);
        throw error;
    }
};

// Eliminar un producto
export const deleteProduct = async (id) => {
    try {
        const product = await db.products.get(id);
        if (!product) {
            throw new Error("Producto no encontrado");
        }

        // Marcar como eliminado y pendiente de sincronización
        await markForDelete("products", id);

        return true;
    } catch (error) {
        console.error(`Error al eliminar producto con ID ${id}:`, error);
        throw error;
    }
}; 